import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '@shared/api/client';
import { DEFAULT_DDW_CONFIG, type DdwConfig } from '@tools/ddw/config';
import './ddw-admin.css';

const COLOR_VARS: ReadonlyArray<{ name: string; label: string; def: string }> = [
  { name: '--primary', label: 'Primary (navy)', def: '#1e3a5f' },
  { name: '--primaryLight', label: 'Primary light', def: '#2d4a6f' },
  { name: '--primaryDark', label: 'Primary dark', def: '#152a45' },
  { name: '--secondary', label: 'Secondary (teal accent)', def: '#0d9488' },
  { name: '--secondaryLight', label: 'Secondary light', def: '#f0fdfa' },
  { name: '--accent', label: 'Accent (electric blue)', def: '#305cde' },
  { name: '--success', label: 'Success', def: '#10b981' },
  { name: '--warning', label: 'Warning', def: '#f59e0b' },
  { name: '--error', label: 'Error', def: '#dc2626' },
];

const SECTIONS: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'brand', label: 'Brand' },
  { id: 'welcome', label: 'Welcome screen' },
  { id: 'redflag', label: 'Red flag words' },
  { id: 'tax', label: 'Tax baseline' },
  { id: 'projection', label: 'Projection multipliers' },
  { id: 'scaleout', label: 'Scale-OUT options' },
  { id: 'scaleup', label: 'Scale-UP options' },
  { id: 'adoption', label: 'Adoption questions' },
  { id: 'spend', label: 'Spend tiers' },
  { id: 'research', label: 'Research questions' },
  { id: 'submission', label: 'Submission' },
  { id: 'data', label: 'Data & reset' },
];

type SaveStatus = 'clean' | 'dirty' | 'saving' | 'error' | 'saved';

/**
 * Admin panel for the DDW tool. Backed by the production draft/publish
 * API (not localStorage anymore). Twelve sections — Brand, Welcome,
 * Red Flag, Tax, Projection, Scale-OUT, Scale-UP, Adoption, Spend tiers,
 * Research, Submission, Data — match the prototype's surface area.
 */
export function DdwAdmin() {
  const [config, setConfig] = useState<DdwConfig>(DEFAULT_DDW_CONFIG);
  const [draftVersion, setDraftVersion] = useState<number | null>(null);
  const [activeSection, setActiveSection] = useState<string>('brand');
  const [status, setStatus] = useState<SaveStatus>('clean');
  const [statusMsg, setStatusMsg] = useState<string>('All changes saved · clients see published edits on next page load');
  const [busy, setBusy] = useState(false);

  // Initial load — pull the latest draft if one exists, otherwise the
  // published config, otherwise just defaults.
  useEffect(() => {
    void (async () => {
      try {
        const draft = await api.getDraft<DdwConfig>('ddw');
        if (draft?.config) {
          setConfig(mergeWithDefaults(draft.config));
          setDraftVersion(draft.version);
          return;
        }
        const pub = await api.getPublishedConfig<DdwConfig>('ddw');
        if (pub.snapshot?.config) {
          setConfig(mergeWithDefaults(pub.snapshot.config));
          setDraftVersion(pub.snapshot.version);
        }
      } catch {
        // No backend? Just keep defaults — admin still works visually.
      }
    })();
  }, []);

  const markDirty = useCallback(() => {
    setStatus('dirty');
    setStatusMsg('Unsaved changes');
  }, []);

  const update = useCallback(
    (mutator: (cfg: DdwConfig) => DdwConfig) => {
      setConfig((prev) => mutator(prev));
      markDirty();
    },
    [markDirty],
  );

  // Sidebar section observer — track which section is in view.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActiveSection(e.target.id);
        });
      },
      { rootMargin: '-20% 0px -70% 0px' },
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const handleSaveDraft = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setStatus('saving');
    setStatusMsg('Saving draft…');
    try {
      const saved = await api.saveDraft<DdwConfig>('ddw', {
        config,
        based_on_version: draftVersion ?? 0,
      });
      setDraftVersion(saved.version);
      setStatus('saved');
      setStatusMsg('Draft saved · ' + new Date().toLocaleTimeString());
    } catch (err) {
      setStatus('error');
      setStatusMsg(err instanceof ApiError ? err.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  }, [busy, config, draftVersion]);

  const handlePublish = useCallback(async () => {
    if (busy) return;
    if (!confirm('Publish these changes? All buyers will see them on next page load.')) return;
    setBusy(true);
    setStatus('saving');
    setStatusMsg('Publishing…');
    try {
      // Save draft first to get the latest version, then publish.
      const saved = await api.saveDraft<DdwConfig>('ddw', {
        config,
        based_on_version: draftVersion ?? 0,
      });
      const published = await api.publish<DdwConfig>('ddw', { based_on_version: saved.version });
      setDraftVersion(published.version);
      setStatus('saved');
      setStatusMsg('Published v' + published.version + ' · ' + new Date().toLocaleTimeString());
    } catch (err) {
      setStatus('error');
      setStatusMsg(err instanceof ApiError ? err.message : 'Publish failed');
    } finally {
      setBusy(false);
    }
  }, [busy, config, draftVersion]);

  const handleExport = useCallback(() => {
    const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'motr-ddw-config-' + new Date().toISOString().slice(0, 10) + '.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [config]);

  const handleImport = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0];
      if (!f) return;
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const incoming = JSON.parse(String(reader.result)) as Partial<DdwConfig>;
          setConfig(mergeWithDefaults(incoming));
          markDirty();
          alert('Imported. Click "Save draft" to persist.');
        } catch (err) {
          alert('Import failed: ' + (err as Error).message);
        }
      };
      reader.readAsText(f);
      e.target.value = '';
    },
    [markDirty],
  );

  const handleReset = useCallback(() => {
    if (
      !confirm(
        'Reset all admin overrides to defaults? You can still cancel by closing the tab without saving.',
      )
    )
      return;
    setConfig({
      ...DEFAULT_DDW_CONFIG,
      content: { ...DEFAULT_DDW_CONFIG.content },
      redFlagWords: [...DEFAULT_DDW_CONFIG.redFlagWords],
      calc: { ...DEFAULT_DDW_CONFIG.calc },
      projection: {
        expectedExpansionsByFlex: { ...DEFAULT_DDW_CONFIG.projection.expectedExpansionsByFlex },
        growthIntensityByFlex: { ...DEFAULT_DDW_CONFIG.projection.growthIntensityByFlex },
      },
      scaleOut: DEFAULT_DDW_CONFIG.scaleOut.map((o) => ({ ...o })),
      scaleUp: DEFAULT_DDW_CONFIG.scaleUp.map((o) => ({ ...o })),
      adoptionQuestions: DEFAULT_DDW_CONFIG.adoptionQuestions.map((q) => ({
        ...q,
        options: q.options.map((o) => ({ ...o })),
      })),
      spendTiers: DEFAULT_DDW_CONFIG.spendTiers.map((t) => ({ ...t })),
      research: { ...DEFAULT_DDW_CONFIG.research },
    });
    markDirty();
  }, [markDirty]);

  return (
    <div className="ddwa">
      <div className="ddwa-subnav">
        <span className="ddwa-subnav-tag">Admin · DDW</span>
        <div className="ddwa-subnav-actions">
          <a href="/ddw">Open public tool</a>
          <button type="button" onClick={handleExport}>
            Export JSON
          </button>
        </div>
      </div>

      <div className="ddwa-layout">
        <aside className="ddwa-sidebar" aria-label="Sections">
          <h3>Sections</h3>
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className={activeSection === s.id ? 'is-active' : ''}
              onClick={(e) => {
                e.preventDefault();
                document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              {s.label}
            </a>
          ))}
        </aside>

        <main className="ddwa-content">
          <BrandSection config={config} update={update} />
          <WelcomeSection config={config} update={update} />
          <RedFlagSection config={config} update={update} />
          <TaxSection config={config} update={update} />
          <ProjectionSection config={config} update={update} />
          <ScaleSection
            id="scaleout"
            title="Scale-OUT options (5b)"
            sub="Penalty multiplier per option. Higher = more cost when adding new workgroups, user groups, use cases, jurisdictions."
            options={config.scaleOut}
            onChange={(next) => update((cfg) => ({ ...cfg, scaleOut: next }))}
          />
          <ScaleSection
            id="scaleup"
            title="Scale-UP options (5c)"
            sub="Penalty multiplier per option. Higher = more cost when adding more users, workflows, or volume in the existing context."
            options={config.scaleUp}
            onChange={(next) => update((cfg) => ({ ...cfg, scaleUp: next }))}
          />
          <AdoptionSection config={config} update={update} />
          <SpendSection config={config} update={update} />
          <ResearchSection config={config} update={update} />
          <SubmissionSection />
          <DataSection onExport={handleExport} onImport={handleImport} onReset={handleReset} />
        </main>
      </div>

      <div className="ddwa-savebar">
        <div className="ddwa-savebar-status">
          <span
            className={`ddwa-savebar-dot${
              status === 'dirty' ? ' is-dirty' : status === 'error' ? ' is-error' : ''
            }`}
          />
          <span>{statusMsg}</span>
        </div>
        <div className="ddwa-savebar-actions">
          <button
            type="button"
            className="ddwa-btn-tertiary"
            onClick={handleSaveDraft}
            disabled={busy}
          >
            Save draft
          </button>
          <button
            type="button"
            className="ddwa-btn-secondary"
            onClick={handlePublish}
            disabled={busy}
          >
            Publish
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Section components ──────────────────────────────────────

interface SectionProps {
  config: DdwConfig;
  update: (mutator: (cfg: DdwConfig) => DdwConfig) => void;
}

function BrandSection({ config, update }: SectionProps) {
  const setVar = (name: string, value: string) =>
    update((cfg) => ({ ...cfg, vars: { ...cfg.vars, [name]: value } }));
  return (
    <section className="ddwa-section" id="brand">
      <h2>Brand</h2>
      <p className="ddwa-section-sub">
        Override the brand colors used across the public tool. Apply on the next page load.
      </p>
      {COLOR_VARS.map((c) => {
        const value = config.vars?.[c.name] ?? c.def;
        return (
          <div key={c.name} className="ddwa-color-row">
            <input
              type="color"
              value={value}
              onChange={(e) => setVar(c.name, e.target.value)}
              aria-label={c.label}
            />
            <label htmlFor={`c-${c.name}`}>{c.label}</label>
            <span className="ddwa-color-hex">{value.toLowerCase()}</span>
          </div>
        );
      })}
    </section>
  );
}

function WelcomeSection({ config, update }: SectionProps) {
  const set = (k: keyof DdwConfig['content'], v: string) =>
    update((cfg) => ({ ...cfg, content: { ...cfg.content, [k]: v } }));
  return (
    <section className="ddwa-section" id="welcome">
      <h2>Welcome screen</h2>
      <p className="ddwa-section-sub">
        Hero copy and the "How it works" cards on the welcome screen.
      </p>
      <div className="ddwa-row">
        <label>Hero badge</label>
        <input
          type="text"
          value={config.content.heroBadge}
          onChange={(e) => set('heroBadge', e.target.value)}
        />
      </div>
      <div className="ddwa-row">
        <label>Hero headline (HTML allowed; wrap accent words in &lt;em&gt;)</label>
        <textarea
          rows={2}
          value={config.content.heroTitle}
          onChange={(e) => set('heroTitle', e.target.value)}
        />
      </div>
      <div className="ddwa-row">
        <label>Hero sub</label>
        <textarea
          rows={3}
          value={config.content.heroSub}
          onChange={(e) => set('heroSub', e.target.value)}
        />
      </div>
      <div className="ddwa-row">
        <label>Primary CTA label</label>
        <input
          type="text"
          value={config.content.ctaPrimary}
          onChange={(e) => set('ctaPrimary', e.target.value)}
        />
      </div>
      <div className="ddwa-row">
        <label>Nav-bar meta text</label>
        <input
          type="text"
          value={config.content.navMeta}
          onChange={(e) => set('navMeta', e.target.value)}
        />
      </div>
    </section>
  );
}

function RedFlagSection({ config, update }: SectionProps) {
  return (
    <section className="ddwa-section" id="redflag">
      <h2>Red Flag trigger words</h2>
      <p className="ddwa-section-sub">
        When the buyer's job statement contains any of these (case-insensitive, word-boundary),
        the soft "you're describing a solution" coaching nudge fires. One word per line.
      </p>
      <div className="ddwa-row">
        <textarea
          rows={8}
          value={config.redFlagWords.join('\n')}
          onChange={(e) =>
            update((cfg) => ({
              ...cfg,
              redFlagWords: e.target.value
                .split('\n')
                .map((s) => s.trim())
                .filter(Boolean),
            }))
          }
        />
      </div>
    </section>
  );
}

function TaxSection({ config, update }: SectionProps) {
  const set = (k: keyof DdwConfig['calc'], v: number) =>
    update((cfg) => ({ ...cfg, calc: { ...cfg.calc, [k]: v } }));
  const num = (k: keyof DdwConfig['calc'], step?: number) => (
    <input
      type="number"
      value={config.calc[k]}
      step={step}
      min={0}
      onChange={(e) => set(k, parseFloat(e.target.value) || 0)}
    />
  );
  return (
    <section className="ddwa-section" id="tax">
      <h2>Adaptation tax — baseline calculation</h2>
      <p className="ddwa-section-sub">
        Defaults that drive the buyer's own baseline tax in Phase 1 Step 5. The vendor-specific
        projection in Phase 2 reuses these.
      </p>
      <div className="ddwa-grid-2">
        <div className="ddwa-row">
          <label>Hours per routine change (baseline)</label>
          {num('hoursPerRoutineChange')}
        </div>
        <div className="ddwa-row">
          <label>Hours per workaround / month</label>
          {num('workaroundHrsPerMonth', 0.25)}
        </div>
        <div className="ddwa-row">
          <label>Hours per major event</label>
          {num('hoursPerMajorEvent')}
        </div>
        <div className="ddwa-row">
          <label>Productivity drag (decimal — e.g. 0.30 for 30%)</label>
          {num('productivityDragPct', 0.05)}
        </div>
        <div className="ddwa-row">
          <label>Drag hours per month</label>
          {num('dragHoursPerMonth')}
        </div>
        <div className="ddwa-row">
          <label>Drag duration (months)</label>
          {num('dragMonths', 0.5)}
        </div>
        <div className="ddwa-row">
          <label>Team median (FTE count)</label>
          {num('teamMedian')}
        </div>
        <div className="ddwa-row">
          <label>Fully-loaded hourly cost ($)</label>
          {num('hourlyCost')}
        </div>
        <div className="ddwa-row">
          <label>Industry benchmark — low ($)</label>
          {num('benchmarkLow')}
        </div>
        <div className="ddwa-row">
          <label>Industry benchmark — high ($)</label>
          {num('benchmarkHigh')}
        </div>
      </div>
    </section>
  );
}

function ProjectionSection({ config, update }: SectionProps) {
  const setExp = (k: 'high' | 'medium' | 'low', v: number) =>
    update((cfg) => ({
      ...cfg,
      projection: {
        ...cfg.projection,
        expectedExpansionsByFlex: { ...cfg.projection.expectedExpansionsByFlex, [k]: v },
      },
    }));
  const setGrowth = (k: 'high' | 'medium' | 'low', v: number) =>
    update((cfg) => ({
      ...cfg,
      projection: {
        ...cfg.projection,
        growthIntensityByFlex: { ...cfg.projection.growthIntensityByFlex, [k]: v },
      },
    }));

  return (
    <section className="ddwa-section" id="projection">
      <h2>Vendor-specific projection multipliers</h2>
      <p className="ddwa-section-sub">
        How the buyer's flexibility level (High/Medium/Low) translates into the expected scale-out
        and scale-up pressure on each vendor.
      </p>
      <h3
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: 'var(--primary)',
          letterSpacing: 0.4,
          textTransform: 'uppercase',
          margin: '4px 0 8px',
        }}
      >
        Expected scale-out events / 24mo
      </h3>
      <div className="ddwa-grid-3">
        {(['high', 'medium', 'low'] as const).map((k) => (
          <div className="ddwa-row" key={`out-${k}`}>
            <label>If lens flex = {k.charAt(0).toUpperCase() + k.slice(1)}</label>
            <input
              type="number"
              value={config.projection.expectedExpansionsByFlex[k]}
              step={0.5}
              min={0}
              onChange={(e) => setExp(k, parseFloat(e.target.value) || 0)}
            />
          </div>
        ))}
      </div>
      <h3
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: 'var(--primary)',
          letterSpacing: 0.4,
          textTransform: 'uppercase',
          margin: '14px 0 8px',
        }}
      >
        Scale-up growth intensity
      </h3>
      <div className="ddwa-grid-3">
        {(['high', 'medium', 'low'] as const).map((k) => (
          <div className="ddwa-row" key={`growth-${k}`}>
            <label>If lens flex = {k.charAt(0).toUpperCase() + k.slice(1)}</label>
            <input
              type="number"
              value={config.projection.growthIntensityByFlex[k]}
              step={0.1}
              min={0}
              onChange={(e) => setGrowth(k, parseFloat(e.target.value) || 0)}
            />
          </div>
        ))}
      </div>
    </section>
  );
}

function ScaleSection({
  id,
  title,
  sub,
  options,
  onChange,
}: {
  id: string;
  title: string;
  sub: string;
  options: DdwConfig['scaleOut'];
  onChange(next: DdwConfig['scaleOut']): void;
}) {
  const update = (idx: number, patch: Partial<DdwConfig['scaleOut'][number]>) => {
    const next = options.map((o, i) => (i === idx ? { ...o, ...patch } : o));
    onChange(next);
  };
  return (
    <section className="ddwa-section" id={id}>
      <h2>{title}</h2>
      <p className="ddwa-section-sub">{sub}</p>
      <div
        className="ddwa-table-row"
        style={{ gridTemplateColumns: '160px 1fr 200px 80px', borderBottom: 'none' }}
      >
        <span style={{ fontSize: 11, color: 'var(--textMuted)', textTransform: 'uppercase' }}>Label</span>
        <span style={{ fontSize: 11, color: 'var(--textMuted)', textTransform: 'uppercase' }}>Sub-text</span>
        <span style={{ fontSize: 11, color: 'var(--textMuted)', textTransform: 'uppercase' }}>Key (read-only)</span>
        <span style={{ fontSize: 11, color: 'var(--textMuted)', textTransform: 'uppercase' }}>Penalty mult</span>
      </div>
      {options.map((o, i) => (
        <div
          key={o.key}
          className="ddwa-table-row"
          style={{ gridTemplateColumns: '160px 1fr 200px 80px' }}
        >
          <input
            type="text"
            value={o.label}
            onChange={(e) => update(i, { label: e.target.value })}
          />
          <input
            type="text"
            value={o.sub}
            onChange={(e) => update(i, { sub: e.target.value })}
          />
          <input type="text" value={o.key} disabled style={{ opacity: 0.5 }} />
          <input
            type="number"
            value={o.penaltyMult}
            step={0.1}
            min={0}
            onChange={(e) => update(i, { penaltyMult: parseFloat(e.target.value) || 0 })}
          />
        </div>
      ))}
    </section>
  );
}

function AdoptionSection({ config, update }: SectionProps) {
  const setQLabel = (qi: number, v: string) =>
    update((cfg) => ({
      ...cfg,
      adoptionQuestions: cfg.adoptionQuestions.map((q, i) => (i === qi ? { ...q, label: v } : q)),
    }));
  const setOpt = (qi: number, oi: number, patch: { label?: string; pts?: number }) =>
    update((cfg) => ({
      ...cfg,
      adoptionQuestions: cfg.adoptionQuestions.map((q, i) =>
        i === qi
          ? { ...q, options: q.options.map((o, j) => (j === oi ? { ...o, ...patch } : o)) }
          : q,
      ),
    }));

  return (
    <section className="ddwa-section" id="adoption">
      <h2>Adoption-risk questions (Section 7)</h2>
      <p className="ddwa-section-sub">
        Per-question, per-option label and point value. Total max = sum of best per-question pts.
        The buyer's confidence % = (sum of selected pts) / (max).
      </p>
      {config.adoptionQuestions.map((q, qi) => (
        <div key={q.key} className="ddwa-adopt-q">
          <input
            type="text"
            value={q.label}
            onChange={(e) => setQLabel(qi, e.target.value)}
            style={{ fontWeight: 600, fontSize: 13 }}
          />
          <div className="ddwa-adopt-options-header">
            <span>Option label</span>
            <span style={{ textAlign: 'right' }}>Points</span>
          </div>
          {q.options.map((o, oi) => (
            <div className="ddwa-adopt-options" key={o.key}>
              <input
                type="text"
                value={o.label}
                onChange={(e) => setOpt(qi, oi, { label: e.target.value })}
              />
              <input
                type="number"
                value={o.pts}
                min={0}
                onChange={(e) => setOpt(qi, oi, { pts: parseInt(e.target.value, 10) || 0 })}
              />
            </div>
          ))}
        </div>
      ))}
    </section>
  );
}

function SpendSection({ config, update }: SectionProps) {
  const setField = (idx: number, patch: { label?: string; sub?: string; annual?: number }) =>
    update((cfg) => ({
      ...cfg,
      spendTiers: cfg.spendTiers.map((t, i) => (i === idx ? { ...t, ...patch } : t)),
    }));

  return (
    <section className="ddwa-section" id="spend">
      <h2>Annual subscription tiers (for value-at-risk projection)</h2>
      <p className="ddwa-section-sub">
        Buyer picks one of these on each vendor. Tier amount × 1.5 = the 18-month subscription
        portion of their spend.
      </p>
      <div
        className="ddwa-table-row"
        style={{ gridTemplateColumns: '120px 1fr 1fr 140px', borderBottom: 'none' }}
      >
        <span style={{ fontSize: 11, color: 'var(--textMuted)', textTransform: 'uppercase' }}>Label</span>
        <span style={{ fontSize: 11, color: 'var(--textMuted)', textTransform: 'uppercase' }}>Sub-text</span>
        <span style={{ fontSize: 11, color: 'var(--textMuted)', textTransform: 'uppercase' }}>Key (read-only)</span>
        <span style={{ fontSize: 11, color: 'var(--textMuted)', textTransform: 'uppercase' }}>Annual ($)</span>
      </div>
      {config.spendTiers.map((t, i) => (
        <div
          key={t.key}
          className="ddwa-table-row"
          style={{ gridTemplateColumns: '120px 1fr 1fr 140px' }}
        >
          <input type="text" value={t.label} onChange={(e) => setField(i, { label: e.target.value })} />
          <input type="text" value={t.sub} onChange={(e) => setField(i, { sub: e.target.value })} />
          <input type="text" value={String(t.key)} disabled style={{ opacity: 0.5 }} />
          <input
            type="number"
            value={t.annual}
            min={0}
            onChange={(e) => setField(i, { annual: parseInt(e.target.value, 10) || 0 })}
          />
        </div>
      ))}
    </section>
  );
}

function ResearchSection({ config, update }: SectionProps) {
  const set = (k: 'q1' | 'q2' | 'q3', v: string) =>
    update((cfg) => ({ ...cfg, research: { ...cfg.research, [k]: v } }));
  return (
    <section className="ddwa-section" id="research">
      <h2>Research opt-in questions</h2>
      <p className="ddwa-section-sub">
        Three short questions surfaced on the Vendor Hub (opt-in).
      </p>
      <div className="ddwa-row">
        <label>Question 1</label>
        <input
          type="text"
          value={config.research.q1}
          onChange={(e) => set('q1', e.target.value)}
        />
      </div>
      <div className="ddwa-row">
        <label>Question 2</label>
        <input
          type="text"
          value={config.research.q2}
          onChange={(e) => set('q2', e.target.value)}
        />
      </div>
      <div className="ddwa-row">
        <label>Question 3</label>
        <input
          type="text"
          value={config.research.q3}
          onChange={(e) => set('q3', e.target.value)}
        />
      </div>
    </section>
  );
}

function SubmissionSection() {
  // The API base URL is hardcoded per build (per locked architectural
  // decision: API base URL is not admin-configurable). Surface that
  // here so admins know they can't override it.
  return (
    <section className="ddwa-section" id="submission">
      <h2>Submission endpoint</h2>
      <p className="ddwa-section-sub">
        The DDW posts anonymous lens + vendor data to the production API. The endpoint URL is
        baked into each build (configured at deploy time, not in the admin panel) — this is
        intentional, so a misconfigured admin user can't redirect submissions.
      </p>
      <div className="ddwa-row">
        <label>API base URL (read-only)</label>
        <input type="text" disabled value={api.baseUrl} />
        <p className="ddwa-hint">Submissions go to <code>{api.baseUrl}/submissions</code></p>
      </div>
    </section>
  );
}

function DataSection({
  onExport,
  onImport,
  onReset,
}: {
  onExport(): void;
  onImport(e: React.ChangeEvent<HTMLInputElement>): void;
  onReset(): void;
}) {
  return (
    <section className="ddwa-section" id="data">
      <h2>Data & reset</h2>
      <p className="ddwa-section-sub">
        Manage the admin config. Resetting only puts the editor back to defaults — your changes are
        only persisted when you click "Save draft" or "Publish" in the bottom bar.
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" className="ddwa-btn-tertiary" onClick={onExport}>
          Export JSON
        </button>
        <label className="ddwa-btn-tertiary" style={{ display: 'inline-flex', alignItems: 'center' }}>
          Import JSON
          <input
            type="file"
            accept=".json,application/json"
            style={{ display: 'none' }}
            onChange={onImport}
          />
        </label>
        <button type="button" className="ddwa-btn-danger" onClick={onReset}>
          Reset all to defaults
        </button>
      </div>
    </section>
  );
}

// ─── Helpers ─────────────────────────────────────────────────

function mergeWithDefaults(incoming: Partial<DdwConfig>): DdwConfig {
  // Use the same merge logic as the public tool's useDdwConfig, but
  // preserve incoming arrays even if empty (admin may have intentionally
  // removed all options — the editor should reflect that). For first
  // load we do prefer defaults to avoid showing an empty editor on
  // missing fields.
  const base = DEFAULT_DDW_CONFIG;
  return {
    content: { ...base.content, ...(incoming.content ?? {}) },
    redFlagWords: incoming.redFlagWords ?? [...base.redFlagWords],
    calc: { ...base.calc, ...(incoming.calc ?? {}) },
    projection: {
      expectedExpansionsByFlex: {
        ...base.projection.expectedExpansionsByFlex,
        ...(incoming.projection?.expectedExpansionsByFlex ?? {}),
      },
      growthIntensityByFlex: {
        ...base.projection.growthIntensityByFlex,
        ...(incoming.projection?.growthIntensityByFlex ?? {}),
      },
    },
    scaleOut: incoming.scaleOut?.length ? incoming.scaleOut : base.scaleOut.map((o) => ({ ...o })),
    scaleUp: incoming.scaleUp?.length ? incoming.scaleUp : base.scaleUp.map((o) => ({ ...o })),
    adoptionQuestions: incoming.adoptionQuestions?.length
      ? incoming.adoptionQuestions
      : base.adoptionQuestions.map((q) => ({
          ...q,
          options: q.options.map((o) => ({ ...o })),
        })),
    spendTiers: incoming.spendTiers?.length
      ? incoming.spendTiers
      : base.spendTiers.map((t) => ({ ...t })),
    research: { ...base.research, ...(incoming.research ?? {}) },
    vars: incoming.vars,
  };
}

